'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Trigger>
>((props, ref) => {
  const innerRef = React.useRef<HTMLButtonElement>(null);
  const composedRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      (innerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    },
    [ref],
  );
  React.useEffect(() => {
    innerRef.current?.removeAttribute('aria-haspopup');
  });
  return <SheetPrimitive.Trigger {...props} ref={composedRef} />;
});
SheetTrigger.displayName = SheetPrimitive.Trigger.displayName;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-[rgba(0,0,0,0.5)]  data-[state=open]_animate-in data-[state=closed]_animate-out data-[state=closed]_fade-out-0 data-[state=open]_fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
));

SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  'fixed z-50 gap-4 bg-white p-6 shadow-lg transition ease-in-out data-[state=open]_animate-in data-[state=closed]_animate-out data-[state=closed]_duration-300 data-[state=open]_duration-500',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0  data-[state=closed]_slide-out-to-top data-[state=open]_slide-in-from-top',
        bottom: 'inset-x-0 bottom-0  data-[state=closed]_slide-out-to-bottom data-[state=open]_slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-full  data-[state=closed]_slide-out-to-left data-[state=open]_slide-in-from-left sm_max-w-sm',
        right:
          'inset-y-0 right-0 h-full w-3/4 data-[state=closed]_slide-out-to-right data-[state=open]_slide-in-from-right',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  showMask?: boolean;
  showCloseButton?: boolean;
  maskClassName?: string;
  closeClassNames?: any;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  (
    {
      side = 'right',
      showMask = true,
      showCloseButton = true,
      maskClassName,
      className,
      closeClassNames,
      children,
      ...props
    },
    ref,
  ) => (
    <SheetPortal>
      {showMask && <SheetOverlay className={maskClassName} />}
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          sheetVariants({
            side,
          }),
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            className={cn(
              'ring-offset-background focus_none absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover_opacity-100 disabled_pointer-events-none data-[state=open]_bg-secondary',
              closeClassNames?.closeClassName,
            )}
          >
            <X
              className={cn('h-4 w-4', closeClassNames?.iconClassName)}
              style={closeClassNames?.iconStyle}
            />
            <span className='sr-only'>Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);

SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col space-y-2 text-center sm_text-left', className)}
    {...props}
  />
);

SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm_flex-row sm_justify-end sm_space-x-2', className)}
    {...props}
  />
);

SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-foreground text-lg font-semibold', className)}
    {...props}
  />
));

SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
));

SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
